/**
 * ResourceRow Component
 * Single row representing one resource with its events in horizontal view
 */

import React, { useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Tooltip, Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import type {
  CalendarEvent,
  Resource,
  SunTimesDay,
} from '@/api/types/calendars.types';
import { getHorizontalEventPosition } from './utils/horizontalEventPosition';
import { calculateEventLayers } from './utils/eventOverlap';
import { HorizontalSunTimesOverlay } from './HorizontalSunTimesOverlay';
import './ResourceRow.css';

interface ResourceRowProps {
  /** Resource to display */
  resource: Resource;
  /** Events for this resource */
  events: CalendarEvent[];
  /** Days being displayed */
  days: Dayjs[];
  /** Time columns */
  timeColumns: { label: string; day: Dayjs; hour: number }[];
  /** Hour resolution (1, 2, or 4 hours) */
  hourResolution: number;
  /** Callback when event is clicked */
  onEventClick?: (event: CalendarEvent) => void;
  /** Callback when time slot is clicked */
  onSlotClick?: (date: Date, resourceId: string, hour: number) => void;
  /** Callback when event drag starts */
  onEventDragStart?: (event: CalendarEvent, e: React.DragEvent) => void;
  /** Callback when dragging over resource row */
  onDragOver?: (e: React.DragEvent) => void;
  /** Callback when drag leaves resource row */
  onDragLeave?: () => void;
  /** Callback when event is dropped */
  onDrop?: (e: React.DragEvent) => void;
  /** ID of currently dragged event */
  draggedEventId?: string;
  /** Drop preview position */
  dropPreview?: { left: number; width: number; hasConflict: boolean } | null;
  /** Sun times data mapped by date */
  sunTimesMap?: Record<string, SunTimesDay>;
  /** Whether to show sun times overlay */
  showDayNight?: boolean;
}

/**
 * PersonAvatar Component
 * Displays person avatar with fallback to initials
 */
const PersonAvatar: React.FC<{
  personId: string;
  personName: string;
  size?: number;
  backgroundColor?: string;
  style?: React.CSSProperties;
}> = ({
  personId,
  personName,
  size = 24,
  backgroundColor = '#667eea',
  style,
}) => {
  const [avatarError, setAvatarError] = React.useState(false);

  const getInitials = () => {
    const nameParts = personName.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return personName.substring(0, 2).toUpperCase();
  };

  return (
    <Avatar
      size={size}
      src={
        !avatarError
          ? `/v2/api/tenants/users/id/${personId}/avatar.jpg?resize_to=64x64`
          : undefined
      }
      icon={avatarError && <UserOutlined />}
      onError={() => {
        setAvatarError(true);
        return false;
      }}
      style={{
        backgroundColor,
        fontSize: size > 24 ? '14px' : '10px',
        fontWeight: 600,
        ...style,
      }}
    >
      {avatarError && getInitials()}
    </Avatar>
  );
};

/**
 * Get resource type icon
 */
const getResourceIcon = (resourceType: string): string => {
  switch (resourceType) {
    case 'AIRPLANE':
      return '✈️';
    case 'INSTRUCTOR':
      return '👨‍✈️';
    case 'PILOT':
      return '🎓';
    case 'AIRPORT':
      return '🏢';
    default:
      return '📍';
  }
};

/**
 * Get event color from airplane resource metadata
 * Fallback to default blue if no airplane or no color
 */
const getEventColor = (event: CalendarEvent): string => {
  const airplane = event.resources?.find((r) => r.resource_type === 'AIRPLANE');
  const color = airplane?.metadata?.calendar_color;
  return color || '#1890ff';
};

/**
 * Generate tooltip content for event
 * Format: Vreme, Student (with avatar), Instruktor (with avatar), Aerodrom
 */
const generateEventTooltip = (
  event: CalendarEvent,
  formatTime: (date: string, format: string) => string,
  formatDate: (date: string, format: string) => string
): React.ReactNode => {
  const airportResource = event.resources?.find(
    (r) => r.resource_type === 'AIRPORT'
  );
  const airportTimezone = airportResource?.resource_timezone;

  // Format: DD.MM.YY HH:mm - HH:mm
  const dateStr = formatDate(event.start_time, 'DD.MM.YY');
  const startTime = formatTime(event.start_time, 'HH:mm');
  const endTime = formatTime(event.end_time, 'HH:mm');

  // Get student and instructor resources
  const studentResource = event.resources?.find((r) =>
    ['PILOT', 'PASSENGER', 'PERSON', 'STUDENT'].includes(r.resource_type)
  );
  const instructorResource = event.resources?.find(
    (r) => r.resource_type === 'INSTRUCTOR'
  );

  // Airport info with timezone
  let airportDisplay = null;
  if (airportResource) {
    const airportName = airportResource.resource_name;
    const shortCode = airportResource.metadata?.icao_code || '';
    let tzInfo = '';
    if (airportTimezone) {
      const tzAbbr = dayjs().tz(airportTimezone).format('z');
      tzInfo = ` (${tzAbbr})`;
    }
    airportDisplay = shortCode
      ? `${airportName} (${shortCode})${tzInfo}`
      : `${airportName}${tzInfo}`;
  }

  return (
    <div style={{ maxWidth: '320px' }}>
      {/* Time row */}
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center' }}>
        <strong style={{ marginRight: 8 }}>Vreme:</strong>
        <span>
          {dateStr} {startTime} - {endTime}
        </span>
      </div>

      {/* Student row with avatar */}
      {studentResource && (
        <div
          style={{
            marginBottom: 8,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <strong style={{ marginRight: 8 }}>Student:</strong>
          <PersonAvatar
            personId={studentResource.resource_id}
            personName={studentResource.resource_name}
            size={20}
            backgroundColor="#52c41a"
            style={{ marginRight: 6 }}
          />
          <span>{studentResource.resource_name}</span>
        </div>
      )}

      {/* Instructor row with avatar */}
      {instructorResource && (
        <div
          style={{
            marginBottom: 8,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <strong style={{ marginRight: 8 }}>Instruktor:</strong>
          <PersonAvatar
            personId={instructorResource.resource_id}
            personName={instructorResource.resource_name}
            size={20}
            backgroundColor="#667eea"
            style={{ marginRight: 6 }}
          />
          <span>{instructorResource.resource_name}</span>
        </div>
      )}

      {/* Airport row */}
      {airportDisplay && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <strong style={{ marginRight: 8 }}>Aerodrom:</strong>
          <span>{airportDisplay}</span>
        </div>
      )}
    </div>
  );
};

/**
 * Get the "other person" to display on event box
 * If row is for instructor -> show student avatar
 * If row is for student -> show instructor avatar
 */
const getOtherPerson = (
  event: CalendarEvent,
  rowResourceType: string
): { id: string; name: string; type: 'student' | 'instructor' } | null => {
  if (rowResourceType === 'INSTRUCTOR') {
    // Show student on instructor's row
    const student = event.resources?.find((r) =>
      ['PILOT', 'PASSENGER', 'PERSON', 'STUDENT'].includes(r.resource_type)
    );
    if (student) {
      return {
        id: student.resource_id,
        name: student.resource_name,
        type: 'student',
      };
    }
  } else if (['PILOT', 'STUDENT'].includes(rowResourceType)) {
    // Show instructor on student's row
    const instructor = event.resources?.find(
      (r) => r.resource_type === 'INSTRUCTOR'
    );
    if (instructor) {
      return {
        id: instructor.resource_id,
        name: instructor.resource_name,
        type: 'instructor',
      };
    }
  }
  return null;
};

export const ResourceRow: React.FC<ResourceRowProps> = ({
  resource,
  events,
  days,
  timeColumns,
  hourResolution,
  onEventClick,
  onSlotClick,
  onEventDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  draggedEventId,
  dropPreview,
  sunTimesMap = {},
  showDayNight = false,
}) => {
  const { parse, formatTime, formatDate, timezone } = useUserTimezone();
  const [avatarError, setAvatarError] = React.useState(false);

  // Get current time for determining if events are in the past
  const currentTime = useMemo(() => dayjs().tz(timezone), [timezone]);

  // Calculate event positions with overlap detection
  const positionedEvents = useMemo(() => {
    // First, calculate horizontal positions
    const eventPositions = events
      .map((event) => {
        const position = getHorizontalEventPosition(
          event,
          days,
          hourResolution,
          parse
        );
        if (!position) return null;
        return { event, position };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    // Then, calculate vertical layers for overlapping events
    const parseToDate = (dateStr: string) => parse(dateStr).toDate();
    return calculateEventLayers(eventPositions, parseToDate);
  }, [events, days, hourResolution, parse]);

  const resourceIcon = getResourceIcon(resource.resource_type);

  // Reset avatar error when resource changes
  React.useEffect(() => {
    setAvatarError(false);
  }, [resource.id]);

  // Get resource initials for avatar fallback
  const getResourceInitials = () => {
    const nameParts = resource.name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return resource.name.substring(0, 2).toUpperCase();
  };

  // Check if this resource is a person (instructor or pilot/student) - to show avatar
  const isPerson =
    resource.resource_type === 'INSTRUCTOR' ||
    resource.resource_type === 'PILOT' ||
    resource.resource_type === 'STUDENT';

  // Different avatar background colors for instructors vs students
  const avatarBackgroundColor =
    resource.resource_type === 'INSTRUCTOR' ? '#667eea' : '#52c41a';

  // Use instance_id (user ID) for avatar, fallback to resource id
  const avatarUserId = resource.instance_id || resource.id;

  return (
    <div className="resource-row">
      {/* Resource label (sticky on left) */}
      <div className="resource-row-label">
        {isPerson ? (
          <Avatar
            size={48}
            src={
              !avatarError
                ? `/v2/api/tenants/users/id/${avatarUserId}/avatar.jpg?resize_to=64x64`
                : undefined
            }
            icon={avatarError && <UserOutlined />}
            onError={() => {
              setAvatarError(true);
              return false;
            }}
            style={{
              backgroundColor: avatarBackgroundColor,
              fontSize: '14px',
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {avatarError && getResourceInitials()}
          </Avatar>
        ) : (
          <span className="resource-icon">{resourceIcon}</span>
        )}
        <span className="resource-name">{resource.name}</span>
      </div>

      {/* Time grid - drop zone */}
      <div
        className="resource-row-grid"
        onDragOver={(e) => {
          if (onDragOver) {
            onDragOver(e);
          }
        }}
        onDragLeave={onDragLeave}
        onDrop={(e) => {
          e.preventDefault();
          if (onDrop) {
            onDrop(e);
          }
        }}
      >
        {/* Time slots */}
        {timeColumns.map((col, index) => (
          <div
            key={index}
            className="resource-time-slot"
            onClick={() =>
              onSlotClick &&
              onSlotClick(
                col.day.hour(col.hour).toDate(),
                resource.id,
                col.hour
              )
            }
          />
        ))}

        {/* Events container */}
        <div className="resource-events-container">
          {/* Sun times overlay - behind events */}
          {showDayNight && Object.keys(sunTimesMap).length > 0 && (
            <HorizontalSunTimesOverlay
              sunTimesMap={sunTimesMap}
              days={days}
              hourResolution={hourResolution}
            />
          )}

          {positionedEvents.map((positioned) => {
            const eventColor = getEventColor(positioned.event);
            const startsBeforeView = positioned.left <= 0;
            const endsAfterView =
              positioned.left + positioned.width >= timeColumns.length * 120;

            // Check if event is finished (end time is in the past)
            const eventEndTime = parse(positioned.event.end_time);
            const isFinished = eventEndTime.isBefore(currentTime);

            // Get the "other person" to display on event box
            const otherPerson = getOtherPerson(
              positioned.event,
              resource.resource_type
            );

            return (
              <Tooltip
                key={positioned.event.id}
                title={generateEventTooltip(
                  positioned.event,
                  formatTime,
                  formatDate
                )}
                placement="top"
                color="rgba(0, 0, 0, 0.85)"
                open={
                  draggedEventId === positioned.event.id ? false : undefined
                }
              >
                <div
                  className={`horizontal-event ${
                    startsBeforeView ? 'continues-left' : ''
                  } ${endsAfterView ? 'continues-right' : ''}`}
                  style={{
                    left: `${positioned.left}px`,
                    width: `${positioned.width}px`,
                    top: `${positioned.top}px`,
                    height: `${positioned.height}px`,
                    background: eventColor,
                    borderLeftColor: eventColor,
                    cursor: onEventDragStart ? 'move' : 'pointer',
                    opacity:
                      draggedEventId === positioned.event.id || isFinished
                        ? 0.5
                        : 1,
                  }}
                  draggable={!!onEventDragStart}
                  onDragStart={(e) => {
                    if (onEventDragStart) {
                      onEventDragStart(positioned.event, e);
                      e.dataTransfer.effectAllowed = 'move';
                    }
                  }}
                  onDragEnd={(e) => {
                    e.preventDefault();
                  }}
                  onClick={() => onEventClick && onEventClick(positioned.event)}
                >
                  <div className="horizontal-event-header">
                    <div className="horizontal-event-time">
                      {startsBeforeView
                        ? '...'
                        : formatTime(positioned.event.start_time, 'HH:mm')}
                    </div>
                    {/* Show the "other person" avatar */}
                    {otherPerson && (
                      <PersonAvatar
                        personId={otherPerson.id}
                        personName={otherPerson.name}
                        size={24}
                        backgroundColor={
                          otherPerson.type === 'instructor'
                            ? '#667eea'
                            : '#52c41a'
                        }
                        style={{
                          border: '2px solid rgba(255,255,255,0.5)',
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </div>
                  <div className="horizontal-event-title">
                    {otherPerson ? otherPerson.name : positioned.event.title}
                  </div>
                </div>
              </Tooltip>
            );
          })}

          {/* Drop preview */}
          {dropPreview && (
            <div
              className={`horizontal-event-drop-preview ${
                dropPreview.hasConflict ? 'conflict' : ''
              }`}
              style={{
                left: `${dropPreview.left}px`,
                width: `${dropPreview.width}px`,
                top: '8px',
                height: '60px',
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
